import jsPDF from 'jspdf';
import JSZip from 'jszip';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import wwLogo from '#client/assets/images/ww-logo-allcompany-black.png';

let cachedLogoDataUrl = null;
async function getLogoDataUrl() {
    if (cachedLogoDataUrl) return cachedLogoDataUrl;
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            cachedLogoDataUrl = canvas.toDataURL('image/png');
            resolve(cachedLogoDataUrl);
        };
        img.onerror = () => resolve(null);
        img.src = wwLogo;
    });
}

async function generateQRDataUrl(voucherCode, uuid) {
    const publicUrl = `${window.location.origin}/voucher/${voucherCode}/${uuid}`;
    return QRCode.toDataURL(publicUrl, { width: 120, margin: 1 });
}

function generateBarcodeDataUrl(code) {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, code, {
        format: 'CODE128',
        width: 3,
        height: 40,
        margin: 5,
        displayValue: true,
        fontSize: 14,
    });
    return canvas.toDataURL('image/png');
}

async function preGenerateImages(vouchers) {
    const qrPromises = vouchers.map((v) => generateQRDataUrl(v.voucherCode, v.uuid));
    const qrDataUrls = await Promise.all(qrPromises);
    const barcodeDataUrls = vouchers.map((v) => generateBarcodeDataUrl(v.voucherCode));
    const imageMap = new Map();
    vouchers.forEach((v, i) => {
        imageMap.set(v.voucherCode, {
            qr: qrDataUrls[i],
            barcode: barcodeDataUrls[i],
        });
    });
    return imageMap;
}

function drawVoucher(pdf, voucher, yOffset, imageMap, logoDataUrl) {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 60;
    const voucherHeight = 250;
    const { voucherCode, firstName = '', lastName = '', distEmailAddress = '', dateStart, dateExpires, program = {}, valueInitial } = voucher;
    const description = program?.programDescription ?? '';
    let y = yOffset + 15;
    if (logoDataUrl) {
        const logoWidth = 140;
        const logoHeight = 44;
        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(logoDataUrl, 'PNG', logoX, y, logoWidth, logoHeight);
        y += logoHeight + 20;
    }
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.text("You've received a Work World Voucher!", pageWidth / 2, y, { align: 'center' });
    y += 10;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.text('Redeem this at any Work World location or online at workworld.com', pageWidth / 2, y, { align: 'center' });
    y += 18;
    const detailsColX = margin + 20;
    const qrColX = pageWidth - margin - 160;
    const detailsColWidth = qrColX - detailsColX - 40;
    let detailsY = y;
    let qrY = y;
    pdf.setFontSize(9);
    const lineHeight = 15;
    const addDetailLine = (label, value) => {
        if (!value) return;
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${label}: `, detailsColX, detailsY);
        const labelWidth = pdf.getTextWidth(`${label}: `);
        pdf.setFont('helvetica', 'normal');
        const maxValueWidth = detailsColWidth - labelWidth;
        const valueLines = pdf.splitTextToSize(String(value), maxValueWidth);
        pdf.text(valueLines, detailsColX + labelWidth, detailsY);
        detailsY += lineHeight * Math.max(1, valueLines.length);
    };
    if (program?.discountAmount != null && Number(program.discountAmount) > 0) {
        const discountText = program.discountType === 'percentage' ? `${program.discountAmount}%` : `$${Number(program.discountAmount).toFixed(2)} off`;
        addDetailLine('Product Discount', discountText);
    }
    if (program?.subsidyType) {
        let creditText = '';
        if (['percentage', 'percentage-subsidy'].includes(program.subsidyType)) {
            creditText = `${Number(valueInitial)}%`;
        } else if (program.subsidyType === 'total-value') {
            creditText = `$${Number(valueInitial).toFixed(2)}`;
        }
        if (creditText) addDetailLine('Program Credit', creditText);
    }
    if (firstName || lastName) {
        addDetailLine('Recipient', `${firstName} ${lastName}`.trim());
    }
    if (program?.freeShipping && program.freeShipping !== 'false') {
        addDetailLine('Free Shipping', 'Included');
    }
    if (program?.taxExempt && program.taxExempt !== 'false') {
        addDetailLine('Tax Exempt', 'Included');
    }
    addDetailLine('Voucher Code', voucherCode);
    if (distEmailAddress) {
        addDetailLine('Email', distEmailAddress);
    }
    addDetailLine('Start Date', new Date(dateStart).toLocaleDateString());
    addDetailLine('Expiration Date', new Date(dateExpires).toLocaleDateString());
    const truncatedDescription = description.length > 150 ? description.slice(0, 150) + '...' : description;
    addDetailLine('Program Description', truncatedDescription);
    const images = imageMap.get(voucherCode);
    if (images) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6);
        pdf.text('Scan to view your voucher online.', qrColX + 65, qrY, { align: 'center' });
        qrY += 10;
        if (images.qr) {
            const qrSize = 70;
            const qrX = qrColX + 65 - qrSize / 2;
            pdf.addImage(images.qr, 'PNG', qrX, qrY, qrSize, qrSize);
            qrY += qrSize + 8;
        }
        if (images.barcode) {
            const barcodeWidth = 160;
            const barcodeHeight = 38;
            const barcodeX = qrColX + 65 - barcodeWidth / 2;
            pdf.addImage(images.barcode, 'PNG', barcodeX, qrY, barcodeWidth, barcodeHeight);
        }
    }
    return voucherHeight;
}

function drawVoucherPage(pdf, pageVouchers, imageMap, logoDataUrl) {
    const voucherHeight = 270;
    const startY = 20;
    pageVouchers.forEach((voucher, index) => {
        const yOffset = startY + index * voucherHeight;
        if (index > 0) {
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.5);
            pdf.line(40, yOffset - 10, pdf.internal.pageSize.getWidth() - 40, yOffset - 10);
        }
        drawVoucher(pdf, voucher, yOffset, imageMap, logoDataUrl);
    });
}

async function generatePDFNative(vouchers, options = {}) {
    const { onPageProgress, startPage = 0, totalPagesOverall = 0, signal } = options;
    const vouchersPerPage = 3;
    const [imageMap, logoDataUrl] = await Promise.all([preGenerateImages(vouchers), getLogoDataUrl()]);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    const totalPages = Math.ceil(vouchers.length / vouchersPerPage);
    for (let i = 0; i < vouchers.length; i += vouchersPerPage) {
        if (signal?.aborted) {
            throw new DOMException('PDF generation cancelled', 'AbortError');
        }
        const pageIndex = Math.floor(i / vouchersPerPage);
        if (onPageProgress) {
            onPageProgress(startPage + pageIndex + 1, totalPagesOverall || totalPages);
        }
        if (i > 0) pdf.addPage();
        const pageVouchers = vouchers.slice(i, i + vouchersPerPage);
        drawVoucherPage(pdf, pageVouchers, imageMap, logoDataUrl);
        if (pageIndex % 20 === 0) {
            await new Promise((r) => setTimeout(r, 0));
        }
    }
    return pdf;
}

export async function renderAndDownloadPDF(vouchers, options = {}) {
    const { onProgress, returnBlob, signal } = options;
    const vouchersPerPage = 3;
    const totalPages = Math.ceil(vouchers.length / vouchersPerPage);
    const VOUCHERS_PER_CHUNK = 300;
    const needsChunking = vouchers.length > VOUCHERS_PER_CHUNK;
    if (!needsChunking) {
        const pdf = await generatePDFNative(vouchers, {
            onPageProgress: onProgress,
            startPage: 0,
            totalPagesOverall: totalPages,
            signal,
        });
        if (onProgress) onProgress(totalPages, totalPages);
        if (returnBlob) {
            return pdf.output('blob');
        } else {
            pdf.save('Vouchers.pdf');
        }
        return;
    }
    const totalChunks = Math.ceil(vouchers.length / VOUCHERS_PER_CHUNK);
    let processedPages = 0;
    const zip = new JSZip();
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        if (signal?.aborted) {
            throw new DOMException('PDF generation cancelled', 'AbortError');
        }
        const chunkStart = chunkIndex * VOUCHERS_PER_CHUNK;
        const chunkEnd = Math.min(chunkStart + VOUCHERS_PER_CHUNK, vouchers.length);
        const chunkVouchers = vouchers.slice(chunkStart, chunkEnd);
        const chunkPages = Math.ceil(chunkVouchers.length / vouchersPerPage);
        const pdf = await generatePDFNative(chunkVouchers, {
            onPageProgress: (current) => {
                if (onProgress) {
                    onProgress(processedPages + current, totalPages);
                }
            },
            startPage: 0,
            totalPagesOverall: chunkPages,
            signal,
        });
        processedPages += chunkPages;
        const pdfBlob = pdf.output('blob');
        const fileName = `Vouchers_Part${chunkIndex + 1}.pdf`;
        zip.file(fileName, pdfBlob);
    }
    if (onProgress) onProgress(totalPages, totalPages);
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(zipBlob);
    link.download = 'Vouchers.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
}
